import React, { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  TextField,
  Box,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  Paper,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import AccountSearchBox, { NEW_ACCOUNT_ID } from "./AccountSearchBox";
import {
  ContactDetails,
  CreateContactErrorResponse,
  ZohoAccount,
  ZohoContact,
} from "../../types/zoho";
import axios, { AxiosError } from "axios";
import { LAMBDA_BASE_URL, zohoCrmContactUrl } from "../../constants";
import { LoadingButton } from "@mui/lab";
import { debounce } from "lodash";
import { alpha } from "@mui/material/styles";
interface Props {
  onSuccess: (contactDetails: ContactDetails) => void;
  /** Renders similar contacts in a separate column outside the form card (parent owns layout). */
  onSimilarAsideChange?: (aside: ReactNode | null) => void;
}
const CONTACT_FULL_NAME_REQUIRED_MESSAGE =
  "Enter first and last name — both are required.";

const PERSONAL_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "aol.com",
  "icloud.com",
  "mail.com",
  "protonmail.com",
  "zoho.com",
  "yandex.com",
];

const getAccountNameFromEmail = (email: string): string | null => {
  const domain = email.split("@")[1];
  if (domain && !PERSONAL_EMAIL_DOMAINS.includes(domain)) {
    return domain.split(".")[0];
  }
  return null;
};

type CreateContactFormField =
  | "contactName"
  | "linkedIn"
  | "primaryEmail"
  | "secondaryEmail";

function zohoFieldApiNameToFormField(
  apiName?: string
): CreateContactFormField | null {
  switch (apiName) {
    case "First_Name":
    case "Last_Name":
      return "contactName";
    case "Contact_LinkedIn_Url":
      return "linkedIn";
    case "Email":
      return "primaryEmail";
    case "Secondary_Email":
      return "secondaryEmail";
    case "Account_Name":
      return null;
    default:
      return null;
  }
}

export function CreateContact({ onSuccess, onSimilarAsideChange }: Props) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fullName = queryParams.get("fullName") || "";
  const email = queryParams.get("email") || "";

  // State variables for form inputs
  const [contactName, setContactName] = useState<string>(
    fullName ? fullName : ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorOnCreate, setErrorOnCreate] = useState<null | string>(null);
  const [serverFieldError, setServerFieldError] = useState<null | {
    field: CreateContactFormField;
    message: string;
  }>(null);
  const [account, setAccount] = useState<ZohoAccount | null>(null);
  const [primaryEmail, setPrimaryEmail] = useState<string>(email);
  const [secondaryEmail, setSecondaryEmail] = useState<string>("");
  const [linkedInUrl, setLinkedInUrl] = useState<string>("");
  const [ownedByCP, setOwnedByCP] = useState<boolean>(false);
  const [similarOnAccount, setSimilarOnAccount] = useState<ZohoContact[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const similarSearchAccountIdRef = useRef<string | null>(null);
  const isLinkedInRequired = PERSONAL_EMAIL_DOMAINS.includes(
    primaryEmail.split("@")[1]
  );
  const isLinkedInValid =
    linkedInUrl.length === 0
      ? true
      : /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-_]+\/?$/.test(
          linkedInUrl.trim()
        );

  const contactNameTokens = contactName.trim().split(/\s+/).filter(Boolean);
  const contactNameHasFirstAndLast = contactNameTokens.length >= 2;

  const debouncedFetchSimilarOnAccount = useMemo(
    () =>
      debounce((acc: ZohoAccount, name: string, mail: string) => {
        axios
          .get<ZohoContact[]>(LAMBDA_BASE_URL, {
            params: {
              similarForCreate: true,
              accountId: acc.id,
              accountName: acc.Account_Name,
              contactName: name.trim(),
              email: mail.trim(),
            },
          })
          .then((res) => setSimilarOnAccount(res.data ?? []))
          .catch(() => setSimilarOnAccount([]))
          .finally(() => setSimilarLoading(false));
      }, 500),
    []
  );

  useEffect(() => {
    if (!account || account.id === NEW_ACCOUNT_ID || !contactNameHasFirstAndLast) {
      debouncedFetchSimilarOnAccount.cancel();
      setSimilarOnAccount([]);
      setSimilarLoading(false);
      similarSearchAccountIdRef.current = null;
      return;
    }
    const accountChanged = similarSearchAccountIdRef.current !== account.id;
    similarSearchAccountIdRef.current = account.id;
    setSimilarLoading(true);
    if (accountChanged) {
      setSimilarOnAccount([]);
    }
    debouncedFetchSimilarOnAccount(account, contactName, primaryEmail);
    return () => {
      debouncedFetchSimilarOnAccount.cancel();
    };
  }, [account, contactName, primaryEmail, contactNameHasFirstAndLast, debouncedFetchSimilarOnAccount]);

  const showSimilarPanel =
    !!account &&
    account.id !== NEW_ACCOUNT_ID &&
    contactNameHasFirstAndLast;

  useEffect(() => {
    if (!onSimilarAsideChange) {
      return;
    }

    const shouldShowAside = showSimilarPanel && similarOnAccount.length > 0;

    if (!shouldShowAside) {
      onSimilarAsideChange(null);
      return;
    }

    onSimilarAsideChange(
      <Paper
        elevation={0}
        sx={(theme) => ({
          p: 2.5,
          height: "100%",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundImage: `linear-gradient(
            165deg,
            ${theme.palette.common.white} 0%,
            ${theme.palette.background.default} 48%,
            ${alpha(theme.palette.primary.main, 0.06)} 100%
          )`,
          boxShadow: theme.shadows[6],
        })}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            mb: 1.5,
            flexShrink: 0,
          }}>
          <Box
            sx={(theme) => ({
              width: 4,
              height: 24,
              borderRadius: 1,
              flexShrink: 0,
              background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.warning.dark})`,
            })}
          />
          <Typography
            component='div'
            variant='subtitle1'
            fontWeight={700}
            color='text.primary'
            sx={{ letterSpacing: 0.15, lineHeight: 1.3 }}>
            Similar contacts on this account
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            mr: -0.25,
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 3,
              backgroundColor: alpha("#000", 0.2),
            },
          }}>
          <Stack spacing={1.25}>
            {similarOnAccount.map((c) => (
              <Box
                key={c.id}
                component='a'
                href={zohoCrmContactUrl(c.id)}
                target='_blank'
                rel='noopener noreferrer'
                sx={(theme) => ({
                  borderRadius: 2,
                  p: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: theme.shadows[1],
                  textDecoration: "none",
                  color: "inherit",
                  display: "block",
                  cursor: "pointer",
                  transition: theme.transitions.create(
                    ["box-shadow", "border-color", "transform"],
                    { duration: theme.transitions.duration.shorter }
                  ),
                  "&:hover": {
                    borderColor: alpha(theme.palette.primary.main, 0.45),
                    boxShadow: theme.shadows[4],
                    transform: "translateY(-1px)",
                  },
                })}>
                <Typography variant='body2' fontWeight={600} color='text.primary'>
                  {c.fullName}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  display='block'
                  sx={{ mt: 0.5 }}>
                  {c.email || "No primary email"}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
        <Box
          sx={(theme) => ({
            flexShrink: 0,
            mt: 1.5,
            pt: 1.75,
            px: 1,
            pb: 0.25,
            mx: -0.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          })}>
          <Typography variant='caption' color='text.secondary' sx={{ display: "block", lineHeight: 1.45 }}>
            If one of these is the same person, use the Search tab to update their
            email instead of creating a duplicate.
          </Typography>
        </Box>
      </Paper>
    );

    return () => onSimilarAsideChange(null);
  }, [onSimilarAsideChange, showSimilarPanel, similarOnAccount]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!contactNameHasFirstAndLast) {
      setServerFieldError({
        field: "contactName",
        message: CONTACT_FULL_NAME_REQUIRED_MESSAGE,
      });
      setErrorOnCreate(null);
      return;
    }
    setIsLoading(true);
    setErrorOnCreate(null);
    setServerFieldError(null);
    try {
      await axios.post<any>(
        LAMBDA_BASE_URL,
        {
          contactName,
          email: primaryEmail,
          secondaryEmail,
          accountName: account!.Account_Name,
          linkedInUrl: linkedInUrl.trim(),
          isNewAccount: account!.id === NEW_ACCOUNT_ID,
          ownedByCP,
        },
        {
          params: {
            isCreate: true,
          },
        }
      );
      setIsLoading(false);
      onSuccess({
        fullName: contactName.trim(),
        account: account!.Account_Name,
      });
    } catch (error) {
      setIsLoading(false);
      if (!account) {
        setErrorOnCreate("Account must be selected.");
        console.error("Error creating contact", error);
        return;
      }
      const data = (error as AxiosError<CreateContactErrorResponse>).response
        ?.data;
      const message =
        data?.message ||
        "Something went wrong while creating the contact. Please try again.";
      const field = zohoFieldApiNameToFormField(data?.fieldApiName);
      if (field && message) {
        setServerFieldError({ field, message });
        setErrorOnCreate(null);
      } else if (data?.fieldApiName === "Account_Name") {
        setErrorOnCreate(
          message ||
            "Pick a company that exists in CRM, or create a new account."
        );
      } else {
        setErrorOnCreate(message);
      }
      console.error("Error creating contact", error);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit} sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          required
          disabled={isLoading}
          label='Contact name'
          variant='outlined'
          fullWidth
          value={contactName}
          onChange={(e) => {
            setContactName(e.target.value);
            if (serverFieldError?.field === "contactName") {
              setServerFieldError(null);
            }
          }}
          error={serverFieldError?.field === "contactName"}
          helperText={
            serverFieldError?.field === "contactName"
              ? serverFieldError.message
              : ""
          }
        />
        <Stack direction='row' gap={2}>
          <TextField
            required
            disabled={isLoading}
            label='Primary Email'
            variant='outlined'
            fullWidth
            value={primaryEmail}
            onChange={(e) => {
              setPrimaryEmail(e.target.value);
              if (serverFieldError?.field === "primaryEmail") {
                setServerFieldError(null);
              }
            }}
            error={serverFieldError?.field === "primaryEmail"}
            helperText={
              serverFieldError?.field === "primaryEmail"
                ? serverFieldError.message
                : ""
            }
          />
          <TextField
            disabled={isLoading}
            label='Secondary Email'
            variant='outlined'
            fullWidth
            value={secondaryEmail}
            onChange={(e) => {
              setSecondaryEmail(e.target.value);
              if (serverFieldError?.field === "secondaryEmail") {
                setServerFieldError(null);
              }
            }}
            error={serverFieldError?.field === "secondaryEmail"}
            helperText={
              serverFieldError?.field === "secondaryEmail"
                ? serverFieldError.message
                : ""
            }
          />
        </Stack>
        <TextField
          required={isLinkedInRequired}
          disabled={isLoading}
          label='Contact LinkedIn Url'
          variant='outlined'
          fullWidth
          value={linkedInUrl}
          onChange={(e) => {
            setLinkedInUrl(e.target.value);
            if (serverFieldError?.field === "linkedIn") {
              setServerFieldError(null);
            }
          }}
          error={!isLinkedInValid || serverFieldError?.field === "linkedIn"}
          helperText={
            serverFieldError?.field === "linkedIn"
              ? serverFieldError.message
              : !isLinkedInValid
                ? "LinkedIn URL must be like: https://www.linkedin.com/in/username/"
                : ""
          }
        />

        <AccountSearchBox
          onChange={(account) => setAccount(account)}
          accountNameFromEmail={getAccountNameFromEmail(email)}
          selectedAccount={account || null}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={ownedByCP}
              onChange={(e) => setOwnedByCP(e.target.checked)}
              disabled={isLoading}
            />
          }
          label='Owned by CP'
        />
        <LoadingButton
          disabled={
            isLoading ||
            !isLinkedInValid ||
            (showSimilarPanel && similarLoading)
          }
          type='submit'
          variant='contained'
          color='primary'
          size='large'
          loading={isLoading || (showSimilarPanel && similarLoading)}>
          Create
        </LoadingButton>
        {errorOnCreate && (
          <Typography variant='body1' color='error' mt='8px'>
            {errorOnCreate}
          </Typography>
        )}
    </Box>
  );
}
